import { cloneDeep } from 'lodash';

import { CellData, CellDataChange, TableChangeSource } from '../../../../../../common/table/TableTypes';

type UndoRedoChange = [row: number, col: number, oldValue: unknown, newValue: unknown];

export enum HotTableUndoRedoActionType {
    CHANGE = 'change',
    FILTER = 'filter',
    INSERT_COLUMN = 'insert_col',
    INSERT_ROW = 'insert_row',
    REMOVE_COLUMN = 'remove_col',
    REMOVE_ROW = 'remove_row',
    ROW_MOVE = 'row_move',
    MILANOTE_META_CELL_DATA_CHANGE = 'milanote_meta_cell_data_change',
    MILANOTE_COLUMN_RESIZE = 'milanote_table_column_resize',
    MILANOTE_MOVE = 'milanote_table_move',
}

export type UndoRedoRichCellDataChange = {
    row: number;
    col: number;
    previousCellData: CellData;
    newCellData: CellData;
};

export type UndoRedoActionWithCellData = {
    // HOT supports actions such as insert/remove row/col, filter, and row move.
    // We currently care about CHANGE for rich-cell replay and MILANOTE_META_CELL_DATA_CHANGE for custom actions.
    actionType?: string;
    // This is the changes that the UndoRedo plugin provides - it's just primitive values
    changes?: UndoRedoChange[];
    // This is the same as the HOT UndoRedo changes, but enriched with the full Milanote CellData for each changed cell
    milanoteCellDataChanges?: UndoRedoRichCellDataChange[];
};

export type UndoRedoRichCellDataState = {
    // The TableChangeSource associated with the undo/redo stack change event
    //   This is purely used to add to or clear out the pendingActionCellDataChanges
    pendingActionSource?: string;
    // Stores the undo/redo cell data changes for the current undo action
    //   This is added to the action in the afterUndoStackChange callback
    pendingActionCellDataChanges?: UndoRedoRichCellDataChange[];
    // A map of a cell coordinate to the replay data to use during undo/redo
    replayCellDataByCoordinate?: Map<string, CellData>;
};

/**
 * Stringifies cell coordinates to create a unique key for storing replay cell data in a Map.
 */
const getCellCoordinateKey = (row: number, col: number): string => `${row}:${col}`;

/**
 * Converts full Milanote cell changes into the compact payload stored alongside HOT change actions.
 */
export const createUndoRedoActionCellDataChanges = (cellDataChanges: CellDataChange[]): UndoRedoRichCellDataChange[] =>
    cellDataChanges.map(([row, col, previousCellData, newCellData]) => ({
        row,
        col: Number(col),
        previousCellData,
        newCellData,
    }));

/**
 * Attaches Milanote cell data to a HOT change action so undo/redo can restore rich cell state.
 */
export const applyUndoRedoActionCellDataChanges = (
    action: UndoRedoActionWithCellData | undefined,
    cellDataChanges: UndoRedoRichCellDataChange[],
): boolean => {
    if (!action || action.actionType !== HotTableUndoRedoActionType.CHANGE) return false;
    if (!action.changes?.length || action.changes.length !== cellDataChanges.length) return false;

    action.milanoteCellDataChanges = cloneDeep(cellDataChanges);
    return true;
};

/**
 * Selects the Milanote cell data that should be replayed for a HOT undo or redo action.
 */
export const getUndoRedoReplayCellDataByCoordinate = (
    source: string,
    action: UndoRedoActionWithCellData | undefined,
): Map<string, CellData> | undefined => {
    const isUndo = source === TableChangeSource.UNDO;
    const isRedo = source === TableChangeSource.REDO;
    if (!isUndo && !isRedo) return;
    if (action?.actionType !== HotTableUndoRedoActionType.CHANGE || !action.changes?.length) return;

    const richCellDataChanges = action?.milanoteCellDataChanges;
    if (!richCellDataChanges?.length) return;

    return new Map(
        richCellDataChanges.map(({ row, col, previousCellData, newCellData }) => [
            getCellCoordinateKey(row, col),
            cloneDeep(isUndo ? previousCellData : newCellData),
        ]),
    );
};

/**
 * Step 1 of the undo/redo flow.
 * Stores the latest Milanote cell changes until HOT finishes building the matching undo stack action.
 */
export const setPendingUndoRedoActionCellDataChanges = (
    undoRedoRichCellDataState: UndoRedoRichCellDataState,
    cellDataChanges: CellDataChange[],
): void => {
    undoRedoRichCellDataState.pendingActionCellDataChanges = createUndoRedoActionCellDataChanges(cellDataChanges);
};

/**
 * Step 2 of the undo/redo flow.
 * Tracks whether the current undo stack mutation is adding a new HOT action or simply moving existing ones.
 */
export const setPendingUndoStackChangeSource = (
    undoRedoRichCellDataState: UndoRedoRichCellDataState,
    source?: string,
): void => {
    undoRedoRichCellDataState.pendingActionSource = source;

    if (source) return;

    delete undoRedoRichCellDataState.pendingActionCellDataChanges;
};

/**
 * Step 2.5 of the undo/redo flow.
 * Enriches the latest HOT undo action with Milanote cell data after HOT pushes it onto the done stack.
 */
export const syncPendingUndoRedoActionToLatestUndoStackEntry = (
    undoRedoRichCellDataState: UndoRedoRichCellDataState,
    doneActionsAfter: UndoRedoActionWithCellData[],
): void => {
    const { pendingActionSource, pendingActionCellDataChanges } = undoRedoRichCellDataState;

    delete undoRedoRichCellDataState.pendingActionSource;
    delete undoRedoRichCellDataState.pendingActionCellDataChanges;

    if (!pendingActionSource || !pendingActionCellDataChanges?.length) return;

    const latestAction = doneActionsAfter[doneActionsAfter.length - 1];
    applyUndoRedoActionCellDataChanges(latestAction, pendingActionCellDataChanges);
};

/**
 * Step 3 of the undo/redo flow.
 * Prepares rich Milanote cell data for the upcoming HOT undo/redo replay.
 */
export const startUndoRedoCellDataReplay = (
    undoRedoRichCellDataState: UndoRedoRichCellDataState,
    source: string,
    action: UndoRedoActionWithCellData | undefined,
): void => {
    const replayCellDataByCoordinate = getUndoRedoReplayCellDataByCoordinate(source, action);

    if (!replayCellDataByCoordinate) {
        delete undoRedoRichCellDataState.replayCellDataByCoordinate;
        return;
    }

    undoRedoRichCellDataState.replayCellDataByCoordinate = replayCellDataByCoordinate;
};

/**
 * Step 4 of the undo/redo flow.
 * Returns replay cell data for the specified cell when HOT is processing an undo/redo change.
 */
export const getUndoRedoReplayCellData = (
    undoRedoRichCellDataState: UndoRedoRichCellDataState,
    row: number,
    col: number,
): CellData | undefined => undoRedoRichCellDataState.replayCellDataByCoordinate?.get(getCellCoordinateKey(row, col));

/**
 * Step 5 of the undo/redo flow.
 * Clears any in-flight rich replay state once HOT has finished applying an undo/redo change.
 */
export const clearUndoRedoCellDataReplay = (undoRedoRichCellDataState: UndoRedoRichCellDataState): void => {
    delete undoRedoRichCellDataState.replayCellDataByCoordinate;
};
